/**
 * Chart renderer — paints inline SVG charts in the chat panel.
 *
 * Four types covered: horizontal_bar, bar, donut (alias pie), scatter.
 * Unknown types fall through to a labelled key/value list so the AI's
 * data never silently drops.
 *
 * Pure inline SVG — no Recharts / Visx / d3 dep.
 *
 * Use cases the AI hits regularly:
 *   - horizontal_bar: cosponsor counts, alignment scores, ranked members
 *   - bar (vertical): same data shape; vertical preferred for short
 *     labels or when the chart sits in a wide column
 *   - donut/pie: party split of bill supporters (40R / 32D / 1I), bloc
 *     cohesion buckets, attention-item urgency mix
 *   - scatter: alignment_pct vs relationship_strength (the "who to
 *     call" quadrant), score-vs-tenure, etc.
 */

import { registerRenderer, type RendererProps } from "./registry.js";
import { Sparkline } from "../charts/Sparkline.js";
import { Dotplot } from "../charts/Dotplot.js";
import { BulletChart } from "../charts/BulletChart.js";

export type ChartType =
  | "horizontal_bar"
  | "bar"
  | "donut"
  | "pie"
  | "scatter"
  // Tufte primitives — same render_chart entry point, different
  // type discriminator. Shape per type:
  //   "sparkline"  → data: [{value}, ...]; options.band?: [lo, hi]
  //   "dotplot"    → data: [{x, label?, color?, category?}, ...];
  //                  options.domain?, options.ticks?, options.categorical?
  //   "bullet"     → data: [{value, target?, bands?: [{upper, fill?}, ...]}]
  //                  one row per chart; multiple rows = stacked bullets
  | "sparkline"
  | "dotplot"
  | "bullet";

export interface ChartDataPoint {
  /** Display label. Either `label` or `name` is read. */
  label?: string;
  name?: string;
  /** Magnitude for bar/donut/pie. Either `value` or `y` is read. */
  value?: number;
  /** x coord for scatter. */
  x?: number;
  /** y coord for scatter; also the magnitude fallback for other types. */
  y?: number;
  /** Optional override color for this point (hex or CSS name). */
  color?: string;
  /** Optional size for scatter (px radius). */
  size?: number;
}

export interface ChartOptions {
  /** Optional palette override. Cycles for additional series. */
  colors?: string[];
  /** Axis labels for scatter. */
  x_label?: string;
  y_label?: string;
  // ── Tufte primitives ────────────────────────────────────────────────
  /** Sparkline: optional [low, high] "normal range" band overlay. */
  band?: [number, number];
  /** Dotplot: explicit axis domain (otherwise inferred from data). */
  domain?: [number, number];
  /** Dotplot: axis ticks. */
  ticks?: Array<{ x: number; label: string }>;
  /** Dotplot: facet vertically by category vs. all-on-one-row. */
  categorical?: boolean;
}

export interface ChartInput {
  type: ChartType;
  title?: string;
  data: ChartDataPoint[];
  options?: ChartOptions;
}

const DEFAULT_BAR_COLOR = "#1f77b4";
const DEFAULT_DONUT_PALETTE = [
  "#1f77b4",
  "#d62728",
  "#2ca02c",
  "#9467bd",
  "#ff7f0e",
  "#17becf",
];

function magnitude(d: ChartDataPoint): number {
  const value = d.value ?? d.y;
  return typeof value === "number" && Number.isFinite(value) ? value : Number.NaN;
}

function display(d: ChartDataPoint): string {
  return String(d.label ?? d.name ?? "");
}

function HorizontalBar({ data, options }: { data: ChartDataPoint[]; options: ChartOptions }) {
  const scale = Math.max(1, ...data.map((d) => Math.abs(magnitude(d))));
  const lo = Math.min(0, ...data.map((d) => magnitude(d) / scale));
  const hi = Math.max(0, ...data.map((d) => magnitude(d) / scale));
  const range = hi - lo || 1;
  const zero = -lo / range * 100;
  return (
    <div className="flex flex-col gap-1">
      {data.map((d, i) => {
        const v = magnitude(d);
        const pct = Math.abs(v / scale) / range * 100;
        const start = (Math.min(0, v / scale) - lo) / range * 100;
        const color = d.color || options.colors?.[i] || DEFAULT_BAR_COLOR;
        return (
          <div
            key={i}
            className="grid items-center gap-2 py-0.5"
            style={{ gridTemplateColumns: "110px 1fr 50px" }}
          >
            <div
              className="text-[11px] text-stone-700 truncate"
              title={display(d)}
            >
              {display(d)}
            </div>
            <div className="relative bg-stone-100 rounded h-3.5 overflow-hidden">
              {lo < 0 && <span className="absolute h-full border-l border-stone-400" style={{ left: `${zero}%` }} />}
              <div
                className="absolute h-full rounded transition-all duration-300"
                style={{ left: `${start}%`, width: `${pct}%`, background: color }}
              />
            </div>
            <div className="text-[11px] font-mono font-semibold text-stone-800 text-right tabular-nums">
              {v.toLocaleString ? v.toLocaleString() : v}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VerticalBar({ data, options }: { data: ChartDataPoint[]; options: ChartOptions }) {
  const w = Math.max(320, data.length * 38);
  const h = 220;
  const left = 36, right = 12, top = 20, bottom = 52;
  const plotHeight = h - top - bottom;
  const scale = Math.max(1, ...data.map((d) => Math.abs(magnitude(d))));
  const lo = Math.min(0, ...data.map((d) => magnitude(d) / scale));
  const hi = Math.max(0, ...data.map((d) => magnitude(d) / scale));
  const range = hi - lo || 1;
  const y = (value: number) => top + (hi - value / scale) / range * plotHeight;
  const zeroY = y(0);
  const step = (w - left - right) / data.length;
  const barWidth = Math.min(36, step * 0.72);
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} style={{ minWidth: w }} role="img" aria-label="Vertical bar chart">
        <title>Vertical bar chart</title>
        <line x1={left} x2={w - right} y1={zeroY} y2={zeroY} stroke="#94a3b8" />
        {data.map((d, i) => {
          const value = magnitude(d), valueY = y(value), x = left + step * (i + 0.5);
          return <g key={i}>
            <rect x={x - barWidth / 2} y={Math.min(valueY, zeroY)} width={barWidth} height={Math.abs(valueY - zeroY)} fill={d.color || options.colors?.[i] || DEFAULT_BAR_COLOR}>
              <title>{`${display(d)}: ${value.toLocaleString()}`}</title>
            </rect>
            <text x={x} y={value >= 0 ? valueY - 5 : valueY + 12} textAnchor="middle" fontSize={10} fill="#334155">{value.toLocaleString()}</text>
            <text x={x} y={h - bottom + 18} textAnchor="end" transform={`rotate(-30 ${x} ${h - bottom + 18})`} fontSize={10} fill="#475569"><title>{display(d)}</title>{display(d).slice(0, 18)}</text>
          </g>;
        })}
      </svg>
    </div>
  );
}

function Donut({ data, options }: { data: ChartDataPoint[]; options: ChartOptions }) {
  // Scale before summing so even large finite values cannot overflow.
  const scale = Math.max(...data.map(magnitude));
  const total = data.reduce((s, d) => s + magnitude(d) / scale, 0);
  const colors = options.colors?.length ? options.colors : DEFAULT_DONUT_PALETTE;
  const r = 50;
  const cx = 70;
  const cy = 70;
  let acc = 0;
  const slices = data
    .map((d, i) => {
      const v = magnitude(d);
      const frac = (v / scale) / total;
      const start = acc * 2 * Math.PI - Math.PI / 2;
      const end = (acc + frac) * 2 * Math.PI - Math.PI / 2;
      acc += frac;
      if (v <= 0) return null;
      // A single SVG arc cannot draw a complete circle with coincident
      // endpoints. Paint a circle for a 100% share instead.
      if (frac >= 1) return <circle key={i} cx={cx} cy={cy} r={r} fill={colors[i % colors.length]}><title>{`${display(d)}: 100%`}</title></circle>;
      const large = frac > 0.5 ? 1 : 0;
      const x1 = cx + r * Math.cos(start);
      const y1 = cy + r * Math.sin(start);
      const x2 = cx + r * Math.cos(end);
      const y2 = cy + r * Math.sin(end);
      return (
        <path
          key={i}
          d={`M ${cx},${cy} L ${x1},${y1} A ${r},${r} 0 ${large} 1 ${x2},${y2} Z`}
          fill={colors[i % colors.length]}
          stroke="white"
          strokeWidth="2"
        />
      );
    })
    .filter(Boolean);
  return (
    <div className="flex items-center gap-3">
      <svg viewBox="0 0 140 140" width="140" height="140" className="shrink-0">
        {slices}
        <circle cx={cx} cy={cy} r={28} fill="white" />
      </svg>
      <div className="flex-1 flex flex-col gap-1 text-[11px]">
        {data.map((d, i) => {
          const v = magnitude(d);
          const pct = Math.round(((v / scale) / total) * 100);
          return (
            <div
              key={i}
              className="grid items-center gap-2"
              style={{ gridTemplateColumns: "12px 1fr auto auto" }}
            >
              <span
                className="w-2.5 h-2.5 rounded-sm"
                style={{ background: colors[i % colors.length] }}
              />
              <span className="truncate">{display(d)}</span>
              <strong className="font-mono">
                {v.toLocaleString ? v.toLocaleString() : v}
              </strong>
              <span className="text-stone-400 font-mono tabular-nums">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Scatter({ data, options }: { data: ChartDataPoint[]; options: ChartOptions }) {
  const xs = data.map((d) => Number(d.x ?? 0));
  const ys = data.map((d) => Number(d.y ?? 0));
  const xScale = Math.max(1, ...xs.map(Math.abs)), yScale = Math.max(1, ...ys.map(Math.abs));
  const xMin = Math.min(...xs.map((x) => x / xScale), 0);
  const xMax = Math.max(...xs.map((x) => x / xScale), 1 / xScale);
  const yMin = Math.min(...ys.map((y) => y / yScale), 0);
  const yMax = Math.max(...ys.map((y) => y / yScale), 1 / yScale);
  const xRange = xMax - xMin || 1;
  const yRange = yMax - yMin || 1;
  const w = 280;
  const h = 180;
  const margin = 24;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h}>
      <rect
        x={margin}
        y={margin}
        width={w - 2 * margin}
        height={h - 2 * margin}
        fill="#f9fafb"
        stroke="#e5e7eb"
      />
      {data.map((d, i) => {
        const x = margin + ((Number(d.x) / xScale - xMin) / xRange) * (w - 2 * margin);
        const y = h - margin - ((Number(d.y) / yScale - yMin) / yRange) * (h - 2 * margin);
        const color = d.color || DEFAULT_BAR_COLOR;
        const r = typeof d.size === "number" && Number.isFinite(d.size) ? Math.max(2, Math.min(30, d.size)) : 4;
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={r}
            fill={color}
            fillOpacity={0.7}
            stroke={color}
          >
            <title>{String(d.label ?? `${d.x}, ${d.y}`)}</title>
          </circle>
        );
      })}
      {options.x_label && (
        <text
          x={w / 2}
          y={h - 4}
          textAnchor="middle"
          fontSize="10"
          fill="#6b7280"
        >
          {options.x_label}
        </text>
      )}
      {options.y_label && (
        <text
          x={10}
          y={h / 2}
          transform={`rotate(-90, 10, ${h / 2})`}
          textAnchor="middle"
          fontSize="10"
          fill="#6b7280"
        >
          {options.y_label}
        </text>
      )}
    </svg>
  );
}

function FallbackList({ data }: { data: ChartDataPoint[] }) {
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      {data.map((d, i) => (
        <div key={i} className="flex justify-between">
          <span>{display(d)}</span>
          <strong className="font-mono">
            {String(d.value ?? d.y ?? "")}
          </strong>
        </div>
      ))}
    </div>
  );
}

function ChartCard({ input }: RendererProps<ChartInput>) {
  const type = (input.type || "horizontal_bar").toLowerCase() as ChartType;
  const supplied = Array.isArray(input.data) ? input.data : [];
  const data = supplied.filter((d) => {
    if (!d || typeof d !== "object" || Array.isArray(d)) return false;
    if (type === "scatter") return Number.isFinite(d.x) && Number.isFinite(d.y);
    if (type === "dotplot") return Number.isFinite(d.x ?? d.y ?? d.value);
    if (type === "donut" || type === "pie" || type === "bullet") return Number.isFinite(magnitude(d)) && magnitude(d) >= 0;
    return Number.isFinite(magnitude(d));
  });
  const omitted = supplied.length - data.length;
  const options = { ...(input.options ?? {}) };
  if (!Array.isArray(options.band) || options.band.length !== 2 || !options.band.every(Number.isFinite) || options.band[0] > options.band[1]) delete options.band;
  if (!Array.isArray(options.domain) || options.domain.length !== 2 || !options.domain.every(Number.isFinite) || options.domain[0] >= options.domain[1]) delete options.domain;
  if (Array.isArray(options.ticks)) options.ticks = options.ticks.filter((tick) => tick && Number.isFinite(tick.x));

  let body: React.ReactNode;
  if (data.length === 0) {
    body = (
      <div className="text-xs italic text-stone-400">No data to render.</div>
    );
  } else if (type === "horizontal_bar") {
    body = <HorizontalBar data={data} options={options} />;
  } else if (type === "bar") {
    body = <VerticalBar data={data} options={options} />;
  } else if (type === "donut" || type === "pie") {
    body = data.some((d) => magnitude(d) > 0) ? <Donut data={data} options={options} /> : <div className="text-xs text-stone-500">No positive values to show. A zero total does not define percentages.</div>;
  } else if (type === "scatter") {
    body = <Scatter data={data} options={options} />;
  } else if (type === "sparkline") {
    // Sparkline: data is a series of values along the x-axis.
    body = (
      <div className="flex items-center gap-2 text-stone-700">
        <Sparkline values={data.map(magnitude)} band={options.band} />
        <span className="text-[11px] font-mono tabular-nums text-stone-500">
          {data.length} pts · last {magnitude(data[data.length - 1])}
        </span>
      </div>
    );
  } else if (type === "dotplot") {
    body = (
      <div className="text-stone-700">
        <Dotplot
          points={data.map((d) => ({
            x: Number(d.x ?? d.y ?? d.value ?? 0),
            label: display(d) || undefined,
            color: d.color,
            // ChartDataPoint doesn't have `category` natively; let
            // callers stuff it on `name` when needed and we read both.
            category: (d as any).category as string | undefined,
          }))}
          domain={options.domain}
          ticks={options.ticks}
          categorical={options.categorical}
        />
      </div>
    );
  } else if (type === "bullet") {
    // Bullet chart: one row per data point, vertically stacked.
    body = (
      <div className="flex flex-col gap-1.5">
        {data.map((d, i) => {
          const v = magnitude(d);
          const targetVal = (d as any).target as number | undefined;
          const bandsVal = (d as any).bands as Array<{ upper: number; fill?: string }> | undefined;
          return (
            <div key={i} className="grid items-center gap-2" style={{ gridTemplateColumns: "110px 1fr 50px" }}>
              <div className="text-[11px] text-stone-700 truncate" title={display(d)}>
                {display(d)}
              </div>
              <BulletChart
                value={v}
                target={targetVal}
                bands={bandsVal}
                barColor={d.color}
              />
              <div className="text-[11px] font-mono font-semibold text-stone-800 text-right tabular-nums">
                {v.toLocaleString ? v.toLocaleString() : v}
              </div>
            </div>
          );
        })}
      </div>
    );
  } else {
    body = <FallbackList data={data} />;
  }

  return (
    <div
      className="self-stretch border border-stone-200 rounded-xl shadow-card bg-white p-3 flex flex-col gap-2"
    >
      {input.title && (
        <div className="text-sm font-semibold text-stone-900">
          {input.title}
        </div>
      )}
      <div>{body}</div>
      {omitted > 0 && <div className="text-[11px] text-stone-500">{omitted} row{omitted === 1 ? "" : "s"} omitted because the required numeric values were missing, invalid or outside this chart's range.</div>}
    </div>
  );
}

registerRenderer<ChartInput>({
  tool: "render_chart",
  label: "AI-rendered chart (bar / donut / scatter)",
  Component: ChartCard,
});

export { ChartCard };
